import { useMemo } from 'react';

import type { CompositionV3Req } from '@revelio/data-access';

import { useCustomSkillTaxonomyTree } from '../../../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { useFilterContext } from '../../../../filters/contexts/FilterProvider';
import { useAvailableDateRangeWithTrialLag } from '../../../../filters/hooks/useAvailableDateRangeWithTrialLag';
import { useEffectiveFilters } from '../../../../filters/hooks/useEffectiveFilters';
import type {
  PrimaryFilterId,
  TreeSelection,
} from '../../../../filters/types/filters';
import { getBaseCompositionV3BreakdownLevels } from '../../../../filters/utils/composition-v3/getCompositionV3BreakdownOptions';
import { getCompositionV3Variables } from '../../../../filters/utils/composition-v3/getCompositionV3Variables';
import { getMonthRefsFromV3Filters } from '../../../../filters/utils/get-v3-month-refs';
import { formatColumnName } from '../../../Chart/chart-menu/data-download/converters/utils';
import { useCompositionV3QueryWithStatus } from '../../../DataResolver/use-composition-v3-query';
import type { TreemapTaxonomyCoverage } from '../../../PageConfigProvider/types/item';
import {
  type DataProviderChildren,
  DataProviderQueryStatus,
} from '../../types';
import { combineQueryStatus } from '../../utils/data-provider-results-utils';
import { useStableReference } from '../../utils/use-stable-reference';
import { amendCompositionV3WithMissingEntities } from '../composition-v3/amend-composition-v3-missing-entities';
import {
  useOverviewSharedRequest,
  withOverviewSharedBreakdowns,
} from '../overview-shared-request';
import {
  convertTaxonomyIcicleData,
  type TaxonomyIcicleData,
  type TaxonomyIcicleResponse,
} from './convert-taxonomy-icicle-data';
import {
  arePageFilterSelectionsEqual,
  buildTaxonomyIcicleRequest,
  isChipCellsRequest,
  pageFilterSelections,
  planTaxonomyIcicleRequests,
  resolveTaxonomyIcicleChipEntity,
  resolveTaxonomyIcicleEntities,
  type TaxonomyIcicleDrill,
  type TaxonomyIcicleRequest,
} from './plan-taxonomy-icicle-requests';
import { ICICLE_TAXONOMIES } from './taxonomies/taxonomy-registry';
import type { IcicleTaxonomy, IcicleTaxonomySegment } from './taxonomies/types';
import { indexTaxonomy } from './taxonomy-icicle-index';
import { useTaxonomyIcicleTree } from './use-taxonomy-icicle-tree';

const isSettled = (status: DataProviderQueryStatus) =>
  status === DataProviderQueryStatus.Success ||
  status === DataProviderQueryStatus.Error;

const useIcicleBreakdown = (
  query: IcicleTaxonomy['query'],
  req: CompositionV3Req | null,
  fallback: CompositionV3Req,
  pause: boolean
) => {
  const [result] = useCompositionV3QueryWithStatus({
    query,
    variables: { req: req ?? fallback },
    pause: pause || req === null,
  });
  const active = req !== null && !pause;
  return {
    active,
    status: result.status,
    data:
      active && result.status === DataProviderQueryStatus.Success
        ? result.data
        : undefined,
  };
};

export const TaxonomyIcicleDataProvider = ({
  segment,
  primaryDimension,
  chips,
  drill,
  taxonomyCoverage,
  children,
}: {
  segment: IcicleTaxonomySegment;
  primaryDimension: PrimaryFilterId;
  chips: readonly TreeSelection[];
  drill: TaxonomyIcicleDrill | null;
  taxonomyCoverage?: TreemapTaxonomyCoverage;
  children: DataProviderChildren<TaxonomyIcicleData>;
}) => {
  const taxonomy = ICICLE_TAXONOMIES[segment];
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const {
    activeFilterMap,
    geographySelectionListIds,
    skillTaxonomyId,
    skillTaxonomyVersion,
  } = useFilterContext();
  const { availableDateRange } = useAvailableDateRangeWithTrialLag();

  const tree = useTaxonomyIcicleTree(taxonomy, chips.length === 0);
  const { levelIds, levelLabels } = tree;
  const index = useMemo(
    () =>
      levelIds === null || tree.treeData.length === 0
        ? null
        : indexTaxonomy(tree.treeData, levelIds),
    [tree.treeData, levelIds]
  );

  const scopedChips = useMemo(
    () =>
      chips.flatMap((chip) => {
        const entity = resolveTaxonomyIcicleChipEntity(
          taxonomy,
          levelIds,
          chip
        );
        return entity === null
          ? []
          : [
              {
                entity,
                chip: {
                  key: `${chip.levelId}:${chip.id}`,
                  name: chip.name,
                  levelId: chip.levelId,
                  id: chip.id,
                },
              },
            ];
      }),
    [taxonomy, levelIds, chips]
  );
  const requestedEntities = useMemo(
    () => scopedChips.map(({ entity }) => entity),
    [scopedChips]
  );

  const filterSelections = useStableReference(
    pageFilterSelections(effectiveFilters, taxonomy.filterId),
    arePageFilterSelectionsEqual
  );
  const entities = useMemo(
    () =>
      levelIds === null
        ? []
        : resolveTaxonomyIcicleEntities({
            taxonomy,
            levelIds,
            chips: scopedChips.map(({ chip }) => chip),
            filterSelections,
          }),
    [taxonomy, levelIds, scopedChips, filterSelections]
  );

  const plan = useMemo(
    () =>
      planTaxonomyIcicleRequests({
        overlapping: taxonomy.overlapping,
        tiersBelowRoot: taxonomy.tiersBelowRoot,
        levelIds: levelIds ?? [],
        entities,
        drill,
      }),
    [taxonomy.overlapping, taxonomy.tiersBelowRoot, levelIds, entities, drill]
  );

  const sharesPageRequest = useOverviewSharedRequest();
  const sharedRequest =
    sharesPageRequest?.ownSegment === taxonomy.segment
      ? sharesPageRequest
      : null;
  // A shared request breaks the other dimensions down at the levels the
  // page's filters call for, as the page's other charts do, and those of the
  // skills are only known once their tree is.
  const { customSkillTree, isCustomSkillTaxonomyPending } =
    useCustomSkillTaxonomyTree(skillTaxonomyId, skillTaxonomyVersion, {
      enabled: sharedRequest !== null,
    });
  const baseRequest = getCompositionV3Variables({
    primaryDimension,
    effectiveFilters,
    isSnapshot: true,
    activeFilterMap,
    compositionEntityScopes: requestedEntities,
    geographySelectionListIds,
    customSkillTree,
    breakdownOptionsOverride: sharedRequest
      ? undefined
      : getBaseCompositionV3BreakdownLevels(),
  });
  const query = sharedRequest ? sharedRequest.query : taxonomy.query;
  const buildRequest = (request: TaxonomyIcicleRequest | undefined) =>
    request === undefined || levelIds === null
      ? null
      : buildTaxonomyIcicleRequest({
          baseRequest,
          taxonomy,
          levelIds,
          depth: request.depth,
          drill: request.scope,
        });
  const requestFor = (request: TaxonomyIcicleRequest | undefined) => {
    const built = buildRequest(request);
    if (built === null || request === undefined || !sharedRequest) {
      return built;
    }
    return withOverviewSharedBreakdowns(built, sharedRequest, {
      tier: isChipCellsRequest(request) ? 'cells' : 'groups',
      owns: taxonomy.segment,
    });
  };

  const { start_date: startDate, end_date: endDate } = baseRequest.filters;
  const datesReady =
    (Boolean(startDate) && Boolean(endDate)) || availableDateRange !== null;
  const monthRefs = useMemo(
    () =>
      getMonthRefsFromV3Filters({ start_date: startDate, end_date: endDate }),
    [startDate, endDate]
  );
  const pauseAll =
    requestedEntities.length === 0 ||
    !datesReady ||
    (sharedRequest !== null && isCustomSkillTaxonomyPending);

  // A plan never holds more than four requests, whatever the taxonomy or drill.
  const requestAt = (slot: number) => requestFor(plan[slot]);
  const fallbackRequest = requestAt(0) ?? baseRequest;
  const slots = [
    useIcicleBreakdown(query, requestAt(0), fallbackRequest, pauseAll),
    useIcicleBreakdown(query, requestAt(1), fallbackRequest, pauseAll),
    useIcicleBreakdown(query, requestAt(2), fallbackRequest, pauseAll),
    useIcicleBreakdown(query, requestAt(3), fallbackRequest, pauseAll),
  ];
  const [dataOne, dataTwo, dataThree, dataFour] = slots.map(
    (slot) => slot.data
  );

  const data = useMemo(() => {
    if (index === null) return undefined;
    const results = [dataOne, dataTwo, dataThree, dataFour];
    const responses = plan.flatMap(
      (request, slot): TaxonomyIcicleResponse[] => {
        const payload = amendCompositionV3WithMissingEntities({
          data: results[slot],
          requestedEntities,
        })?.composition_v3;
        return payload === undefined || payload === null
          ? []
          : [{ ...request, payload }];
      }
    );
    return convertTaxonomyIcicleData({
      segment: taxonomy.segment,
      overlapping: taxonomy.overlapping,
      tiersBelowRoot: taxonomy.tiersBelowRoot,
      index,
      entities,
      responses,
      drill,
      taxonomyCoverage,
      entityLabel: formatColumnName(primaryDimension),
      levelLabels,
    });
  }, [
    index,
    entities,
    drill,
    taxonomyCoverage,
    primaryDimension,
    levelLabels,
    taxonomy,
    requestedEntities,
    plan,
    dataOne,
    dataTwo,
    dataThree,
    dataFour,
  ]);

  const status = (() => {
    const activeSlots = slots.filter(({ active }) => active);
    if (tree.fetching) return DataProviderQueryStatus.Fetching;
    const treeMissing =
      tree.treeData.length === 0 && (tree.error !== undefined || !pauseAll);
    if (levelIds === null || treeMissing) {
      return DataProviderQueryStatus.Error;
    }
    if (pauseAll || activeSlots.some(({ status }) => !isSettled(status))) {
      return DataProviderQueryStatus.Fetching;
    }
    return combineQueryStatus(activeSlots.map(({ status }) => status));
  })();

  return children({ data, status, monthRefs });
};
